/**
 * 서비스 관리 Presenter 컴포넌트
 * UI 렌더링만 담당합니다.
 */
import React from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ViewToggle } from '@/components/ui/ViewToggle';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Label } from '@/components/ui/Label';
import { AlertMessage } from '@/components/ui/AlertMessage';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { X, Search, Trash2, CheckSquare, Pause, Play, Eye, EyeOff, Plus } from 'lucide-react';
import { ServiceGridView } from '@/components/services/management/superadmin/ServiceGridView';
import { ServiceTableView } from '@/components/services/management/superadmin/ServiceTableView';
import { SERVICE_CATEGORIES, CATEGORY_LABELS } from '@/constants/serviceCategories';
import { MSAService } from '@/types';
import { HealthStatus } from '@/types/health';

interface ServiceManagementViewProps {
  // State
  activeTab: string;
  view: 'card' | 'table';
  loading: boolean;
  error: string;
  success: string;
  serviceCounts: Record<string, number>;
  filteredServices: MSAService[];
  healthStatuses: Record<number, HealthStatus>;
  // Filters
  searchText: string;
  filterCategory: string;
  filterVisibility: string;
  filterStatus: string;
  // Actions
  onTabChange: (tab: string) => void;
  onViewChange: (view: 'card' | 'table') => void;
  onRefresh: () => void;
  /** 서비스 등록 다이얼로그를 연다 (#136) */
  onRegisterClick: () => void;
  onSearchTextChange: (text: string) => void;
  onFilterCategoryChange: (category: string) => void;
  onFilterVisibilityChange: (visibility: string) => void;
  onFilterStatusChange: (status: string) => void;
  onResetFilters: () => void;
  onClearAllClick: () => void;
  onErrorClose: () => void;
  onSuccessClose: () => void;
  onApproveClick: (serviceId: number) => void;
  onReject: (id: number, name: string) => void;
  onSuspend: (id: number, name: string) => void;
  onResume: (id: number, name: string) => void;
  onDelete: (id: number, name: string) => void;
  onRestore: (id: number, name: string) => void;
  onCancelRejection: (id: number, name: string) => void;
  onToggleVisibility: (id: number, name: string, newVisibility: boolean) => void;
  onServiceClick: (service: MSAService) => void;
  onManagePlans: (service: MSAService) => void;
  /** 로그인 연동(OIDC 클라이언트) 발급 · 관리 (#134) */
  onManageClient: (service: MSAService) => void;
  getFilteredServices: (tab: string) => MSAService[];
  // Bulk actions
  selectedIds?: Set<number>;
  onSelectionChange?: (ids: Set<number>) => void;
  onBulkApprove?: () => void;
  onBulkSuspend?: () => void;
  onBulkResume?: () => void;
  onBulkDelete?: () => void;
  onBulkSetPublic?: () => void;
  onBulkSetPrivate?: () => void;
}

export const ServiceManagementView: React.FC<ServiceManagementViewProps> = ({
  activeTab,
  view,
  loading,
  error,
  success,
  serviceCounts,
  filteredServices: _filteredServices, // 사용되지 않지만 하위 호환성을 위해 유지
  healthStatuses,
  searchText,
  filterCategory,
  filterVisibility,
  filterStatus,
  onTabChange,
  onViewChange,
  onRefresh,
  onRegisterClick,
  onSearchTextChange,
  onFilterCategoryChange,
  onFilterVisibilityChange,
  onFilterStatusChange,
  onResetFilters,
  onClearAllClick,
  onErrorClose,
  onSuccessClose,
  onApproveClick,
  onReject,
  onSuspend,
  onResume,
  onDelete,
  onRestore,
  onCancelRejection,
  onToggleVisibility,
  onServiceClick,
  onManagePlans,
  onManageClient,
  getFilteredServices,
  selectedIds,
  onSelectionChange,
  onBulkApprove,
  onBulkSuspend,
  onBulkResume,
  onBulkDelete,
  onBulkSetPublic,
  onBulkSetPrivate,
}) => {
  const renderServiceGrid = (tab: string, isReadOnly: boolean = false) => {
    const services = getFilteredServices(tab);
    const commonProps = {
      services,
      healthStatuses,
      loading,
      activeTab: tab,
      isReadOnly,
      onApproveClick,
      onReject,
      onSuspend,
      onResume,
      onDelete,
      onRestore,
      onCancelRejection,
      onToggleVisibility,
      onServiceClick,
      onManagePlans,
      onManageClient,
    };

    // pending/approved 탭에서 체크박스 표시
    const showCheckboxes = tab === 'pending' || tab === 'approved';
    const tableProps = showCheckboxes ? {
      ...commonProps,
      selectedIds,
      onSelectionChange,
    } : commonProps;

    if (view === 'card') {
      return <ServiceGridView {...commonProps} />;
    }

    return <ServiceTableView {...tableProps} />;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">MSA 서비스 관리</h2>
          <p className="text-sm text-gray-500 mt-1">등록된 MSA 서비스를 관리하고 승인합니다</p>
        </div>
        <div className="flex items-center space-x-2">
          {/* 관리자가 직접 등록한다 (#136) */}
          <Button onClick={onRegisterClick}>
            <Plus className="w-4 h-4 mr-1" />
            서비스 등록
          </Button>
          <Button
            onClick={onRefresh}
            variant="outline"
            disabled={loading}
          >
            {loading ? (
              <div className="flex items-center space-x-2">
                <LoadingSpinner size="sm" />
                <span>새로고침 중...</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>상태 새로고침</span>
              </div>
            )}
          </Button>
          <ViewToggle view={view} onViewChange={onViewChange} />
        </div>
      </div>

      {error && (
        <AlertMessage 
          type="error" 
          message={error} 
          onClose={onErrorClose} 
        />
      )}

      {success && (
        <AlertMessage 
          type="success" 
          message={success} 
          onClose={onSuccessClose} 
        />
      )}

      <Tabs value={activeTab} onValueChange={(v) => {
        onTabChange(v);
        onErrorClose();
        onSuccessClose();
      }}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="all">전체 (읽기전용) ({serviceCounts.all})</TabsTrigger>
          <TabsTrigger value="approved">등록된 서비스 ({serviceCounts.approved + serviceCounts.suspended})</TabsTrigger>
          <TabsTrigger value="pending">등록대기중 ({serviceCounts.pending})</TabsTrigger>
          <TabsTrigger value="deleted_rejected">삭제/거부됨 ({serviceCounts.deleted_rejected})</TabsTrigger>
        </TabsList>

        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* 공개/비공개 필터 */}
            <div>
              <Label htmlFor="filter-visibility">공개</Label>
              <Select value={filterVisibility} onValueChange={onFilterVisibilityChange}>
                <SelectTrigger id="filter-visibility" className="h-10">
                  <SelectValue placeholder="전체" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem key="service-visibility-all" value="all">전체</SelectItem>
                  <SelectItem key="service-visibility-public" value="public">공개</SelectItem>
                  <SelectItem key="service-visibility-private" value="private">비공개</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* 카테고리 필터 */}
            <div>
              <Label htmlFor="filter-category">카테고리</Label>
              <Select value={filterCategory} onValueChange={onFilterCategoryChange}>
                <SelectTrigger id="filter-category" className="h-10">
                  <SelectValue placeholder="모든 카테고리" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem key="service-category-all" value="all">모든 카테고리</SelectItem>
                  {SERVICE_CATEGORIES.map((category) => (
                    <SelectItem key={`service-category-${category}`} value={category}>
                      {CATEGORY_LABELS[category]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* 통합 검색 */}
            <div className="lg:col-span-2">
              <Label htmlFor="search-text">검색</Label>
              <Input
                id="search-text"
                type="text"
                placeholder="서비스명, 설명, 개발사로 검색..."
                value={searchText}
                onChange={(e) => onSearchTextChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    // 검색은 실시간으로 적용되므로 별도 액션 불필요
                  }
                }}
                className="w-full h-10"
              />
            </div>
            
            {/* 상태 필터 */}
            <div>
              <Label htmlFor="filter-status">상태</Label>
              <Select value={filterStatus} onValueChange={onFilterStatusChange}>
                <SelectTrigger id="filter-status" className="h-10">
                  <SelectValue placeholder="전체" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem key="service-status-all" value="all">전체</SelectItem>
                  <SelectItem key="service-status-pending" value="pending">등록대기중</SelectItem>
                  <SelectItem key="service-status-approved" value="approved">등록됨</SelectItem>
                  <SelectItem key="service-status-suspended" value="suspended">일시중지</SelectItem>
                  <SelectItem key="service-status-rejected" value="rejected">거부됨</SelectItem>
                  <SelectItem key="service-status-deleted" value="deleted">삭제됨</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* 초기화 및 검색 버튼 */}
          <div className="flex justify-end gap-2">
            {activeTab === 'deleted_rejected' && (
              <Button
                variant="destructive"
                onClick={onClearAllClick}
                disabled={loading}
                className="h-10"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                모두 클리어
              </Button>
            )}
            <Button
              variant="outline"
              onClick={onResetFilters}
              disabled={loading}
              className="h-10"
            >
              <X className="mr-2 h-4 w-4" />
              초기화
            </Button>
            <Button
              onClick={() => {
                // 검색은 실시간으로 적용되므로 별도 액션 불필요
              }}
              disabled={loading}
              className="h-10"
            >
              <Search className="mr-2 h-4 w-4" />
              검색
            </Button>
          </div>
        </div>

        <TabsContent value="pending" className="space-y-4">
          {selectedIds && selectedIds.size > 0 && (
            <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <CheckSquare className="h-4 w-4 text-blue-600" />
              <span className="text-sm text-blue-800 font-medium">
                {selectedIds.size}개 서비스 선택됨
              </span>
              <Button
                size="sm"
                onClick={onBulkApprove}
                disabled={loading}
              >
                선택 일괄 승인
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onSelectionChange?.(new Set())}
              >
                선택 해제
              </Button>
            </div>
          )}
          {renderServiceGrid('pending')}
        </TabsContent>

        <TabsContent value="approved" className="space-y-4">
          {selectedIds && selectedIds.size > 0 && (
            <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-md flex-wrap">
              <CheckSquare className="h-4 w-4 text-blue-600" />
              <span className="text-sm text-blue-800 font-medium">
                {selectedIds.size}개 서비스 선택됨
              </span>
              <div className="flex gap-1 ml-2">
                <Button size="sm" variant="warning" onClick={onBulkSuspend} disabled={loading}>
                  <Pause className="w-3.5 h-3.5 mr-1" />일시중지
                </Button>
                <Button size="sm" variant="outline" onClick={onBulkResume} disabled={loading}>
                  <Play className="w-3.5 h-3.5 mr-1" />재개
                </Button>
                <Button size="sm" variant="outline" onClick={onBulkSetPublic} disabled={loading}>
                  <Eye className="w-3.5 h-3.5 mr-1" />공개
                </Button>
                <Button size="sm" variant="outline" onClick={onBulkSetPrivate} disabled={loading}>
                  <EyeOff className="w-3.5 h-3.5 mr-1" />비공개
                </Button>
                <Button size="sm" variant="destructive" onClick={onBulkDelete} disabled={loading}>
                  <Trash2 className="w-3.5 h-3.5 mr-1" />삭제
                </Button>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onSelectionChange?.(new Set())}
                className="ml-auto"
              >
                선택 해제
              </Button>
            </div>
          )}
          {renderServiceGrid('approved')}
        </TabsContent>

        <TabsContent value="all" className="space-y-4">
          {renderServiceGrid('all', true)}
        </TabsContent>

        <TabsContent value="deleted_rejected" className="space-y-4">
          {renderServiceGrid('deleted_rejected')}
        </TabsContent>
      </Tabs>
    </div>
  );
};
